/**
 * Bliss Bedrooms Delivery & Distance Calculation
 * Calculates distance in miles from the Bliss shop/home in Santa Rosa Beach, FL 32459
 * and determines the distance-based delivery and freight allowance.
 */

export const BLISS_HOME_BASE = {
  zip: '32459',
  city: 'Santa Rosa Beach',
  state: 'FL',
  lat: 30.3949,
  lng: -86.2286,
}

// Haversine formula
function toRadians(degrees: number): number {
  return degrees * (Math.PI / 180)
}

export function haversineDistanceMiles(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 3959 // Earth radius in miles
  const dLat = toRadians(lat2 - lat1)
  const dLng = toRadians(lng2 - lng1)
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) * Math.sin(dLng / 2) * Math.sin(dLng / 2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  return Math.round(R * c)
}

type LatLng = { lat: number; lng: number }

const ZIP3_COORDINATES: Record<string, LatLng> = {
  // Florida Panhandle & Local
  '324': { lat: 30.3949, lng: -86.2286 }, // Santa Rosa Beach / 30A / Panama City (0 mi)
  '325': { lat: 30.4213, lng: -87.2169 }, // Pensacola / Destin / Fort Walton
  '323': { lat: 30.4383, lng: -84.2807 }, // Tallahassee
  // Florida Regions
  '320': { lat: 30.3322, lng: -81.6557 }, // Jacksonville
  '321': { lat: 29.2108, lng: -81.0228 }, // Daytona Beach
  '322': { lat: 30.3322, lng: -81.6557 }, // Jacksonville metro
  '326': { lat: 29.6516, lng: -82.3248 }, // Gainesville
  '327': { lat: 28.5383, lng: -81.3792 }, // Orlando North
  '328': { lat: 28.5383, lng: -81.3792 }, // Orlando metro
  '329': { lat: 28.0836, lng: -80.6081 }, // Melbourne / Space Coast
  '330': { lat: 25.7617, lng: -80.1918 }, // Florida Keys / Miami
  '331': { lat: 25.7617, lng: -80.1918 }, // Miami
  '332': { lat: 25.7617, lng: -80.1918 }, // Miami
  '333': { lat: 26.1224, lng: -80.1373 }, // Fort Lauderdale
  '334': { lat: 26.7153, lng: -80.0534 }, // West Palm Beach
  '335': { lat: 27.9506, lng: -82.4572 }, // Tampa East
  '336': { lat: 27.9506, lng: -82.4572 }, // Tampa metro
  '337': { lat: 27.7676, lng: -82.6403 }, // St. Petersburg
  '338': { lat: 28.0395, lng: -81.9498 }, // Lakeland
  '339': { lat: 26.6406, lng: -81.8723 }, // Fort Myers
  '341': { lat: 26.1420, lng: -81.7948 }, // Naples
  '342': { lat: 27.3364, lng: -82.5307 }, // Sarasota
  '344': { lat: 29.1872, lng: -82.1401 }, // Ocala
  '346': { lat: 28.3283, lng: -82.6848 }, // Pasco / Spring Hill
  '347': { lat: 28.3000, lng: -81.4100 }, // Kissimmee
  '349': { lat: 27.4467, lng: -80.3256 }, // Fort Pierce / Port St. Lucie

  // Alabama
  '363': { lat: 31.2232, lng: -85.3905 }, // Dothan
  '364': { lat: 31.3088, lng: -86.4833 }, // Andalusia
  '365': { lat: 30.6954, lng: -88.0399 }, // Mobile / Baldwin County / Gulf Shores
  '366': { lat: 30.6954, lng: -88.0399 }, // Mobile metro
  '360': { lat: 32.3792, lng: -86.3077 }, // Montgomery
  '361': { lat: 32.3792, lng: -86.3077 }, // Montgomery metro
  '367': { lat: 32.4074, lng: -87.0211 }, // Selma
  '368': { lat: 32.6099, lng: -85.4808 }, // Auburn / Opelika
  '350': { lat: 33.5186, lng: -86.8104 }, // Birmingham area
  '351': { lat: 33.5186, lng: -86.8104 }, // Birmingham area
  '352': { lat: 33.5186, lng: -86.8104 }, // Birmingham metro
  '354': { lat: 33.2098, lng: -87.5692 }, // Tuscaloosa
  '356': { lat: 34.6059, lng: -86.9833 }, // Decatur
  '357': { lat: 34.7304, lng: -86.5861 }, // Huntsville area
  '358': { lat: 34.7304, lng: -86.5861 }, // Huntsville metro
  '359': { lat: 34.0143, lng: -86.0066 }, // Gadsden
  '362': { lat: 33.6598, lng: -85.8316 }, // Anniston

  // Georgia
  '316': { lat: 30.8327, lng: -83.2785 }, // Valdosta
  '317': { lat: 31.5785, lng: -84.1557 }, // Albany / Thomasville
  '318': { lat: 32.4610, lng: -84.9877 }, // Columbus
  '319': { lat: 32.4610, lng: -84.9877 }, // Columbus metro
  '398': { lat: 30.9038, lng: -84.5755 }, // Bainbridge
  '310': { lat: 32.8407, lng: -83.6324 }, // Macon area
  '312': { lat: 32.8407, lng: -83.6324 }, // Macon metro
  '313': { lat: 32.0809, lng: -81.0912 }, // Savannah area
  '314': { lat: 32.0809, lng: -81.0912 }, // Savannah metro
  '315': { lat: 31.1500, lng: -81.4900 }, // Brunswick / Golden Isles
  '300': { lat: 33.7490, lng: -84.3880 }, // Atlanta North
  '301': { lat: 33.7490, lng: -84.3880 }, // Atlanta West
  '302': { lat: 33.7490, lng: -84.3880 }, // Atlanta South
  '303': { lat: 33.7490, lng: -84.3880 }, // Atlanta Central
  '311': { lat: 33.7490, lng: -84.3880 }, // Atlanta
  '399': { lat: 33.7490, lng: -84.3880 }, // Atlanta
  '304': { lat: 32.4488, lng: -81.7832 }, // Statesboro
  '305': { lat: 34.2979, lng: -83.8241 }, // Gainesville, GA
  '306': { lat: 33.9519, lng: -83.3576 }, // Athens, GA
  '307': { lat: 34.7698, lng: -84.9702 }, // Dalton
  '308': { lat: 33.4735, lng: -81.9676 }, // Augusta area
  '309': { lat: 33.4735, lng: -81.9676 }, // Augusta metro

  // Mississippi
  '395': { lat: 30.3674, lng: -89.0928 }, // Gulfport / Biloxi
  '394': { lat: 31.3157, lng: -89.2895 }, // Hattiesburg
  '393': { lat: 32.3643, lng: -88.7037 }, // Meridian
  '390': { lat: 32.2988, lng: -90.1848 }, // Jackson area
  '391': { lat: 32.2988, lng: -90.1848 }, // Jackson area
  '392': { lat: 32.2988, lng: -90.1848 }, // Jackson metro
  '396': { lat: 31.2438, lng: -90.4532 }, // McComb
  '397': { lat: 33.4957, lng: -88.4273 }, // Columbus / Starkville
  '386': { lat: 34.3665, lng: -89.5192 }, // Oxford
  '387': { lat: 33.4082, lng: -91.0601 }, // Greenville, MS
  '388': { lat: 34.2576, lng: -88.7034 }, // Tupelo
  '389': { lat: 33.5162, lng: -90.1795 }, // Greenwood

  // Louisiana
  '700': { lat: 29.9511, lng: -90.0715 }, // New Orleans area
  '701': { lat: 29.9511, lng: -90.0715 }, // New Orleans metro
  '704': { lat: 30.4755, lng: -90.1009 }, // Covington / Slidell / Mandeville
  '703': { lat: 29.5958, lng: -90.7195 }, // Houma / Thibodaux
  '707': { lat: 30.4515, lng: -91.1871 }, // Baton Rouge area
  '708': { lat: 30.4515, lng: -91.1871 }, // Baton Rouge metro
  '705': { lat: 30.2241, lng: -92.0198 }, // Lafayette
  '706': { lat: 30.2266, lng: -93.2174 }, // Lake Charles
  '710': { lat: 32.5252, lng: -93.7502 }, // Shreveport area
  '711': { lat: 32.5252, lng: -93.7502 }, // Shreveport metro
  '712': { lat: 32.5093, lng: -92.1193 }, // Monroe
  '713': { lat: 31.3113, lng: -92.4451 }, // Alexandria
  '714': { lat: 31.7607, lng: -93.0868 }, // Natchitoches

  // Tennessee
  '373': { lat: 35.0456, lng: -85.3097 }, // Chattanooga area
  '374': { lat: 35.0456, lng: -85.3097 }, // Chattanooga metro
  '370': { lat: 36.1627, lng: -86.7816 }, // Nashville area
  '371': { lat: 36.1627, lng: -86.7816 }, // Nashville area
  '372': { lat: 36.1627, lng: -86.7816 }, // Nashville metro
  '376': { lat: 36.3134, lng: -82.3535 }, // Johnson City
  '377': { lat: 35.9606, lng: -83.9207 }, // Knoxville area
  '378': { lat: 35.9606, lng: -83.9207 }, // Knoxville area
  '379': { lat: 35.9606, lng: -83.9207 }, // Knoxville metro
  '380': { lat: 35.1495, lng: -90.0490 }, // Memphis area
  '381': { lat: 35.1495, lng: -90.0490 }, // Memphis metro
  '382': { lat: 36.3434, lng: -88.8503 }, // Martin
  '383': { lat: 35.6145, lng: -88.8139 }, // Jackson, TN
  '384': { lat: 35.6151, lng: -87.0353 }, // Columbia, TN
  '385': { lat: 36.1628, lng: -85.5016 }, // Cookeville

  // South Carolina
  '299': { lat: 32.2163, lng: -80.7526 }, // Hilton Head / Bluffton / Beaufort
  '294': { lat: 32.7765, lng: -79.9311 }, // Charleston
  '290': { lat: 34.0007, lng: -81.0348 }, // Columbia area
  '291': { lat: 34.0007, lng: -81.0348 }, // Columbia area
  '292': { lat: 34.0007, lng: -81.0348 }, // Columbia metro
  '293': { lat: 34.9496, lng: -81.9320 }, // Spartanburg
  '295': { lat: 33.6891, lng: -78.8867 }, // Myrtle Beach / Florence
  '296': { lat: 34.8526, lng: -82.3940 }, // Greenville
  '297': { lat: 34.9249, lng: -81.0259 }, // Rock Hill
  '298': { lat: 33.5604, lng: -81.7196 }, // Aiken

  // North Carolina
  '280': { lat: 35.2271, lng: -80.8431 }, // Charlotte area
  '281': { lat: 35.2271, lng: -80.8431 }, // Charlotte area
  '282': { lat: 35.2271, lng: -80.8431 }, // Charlotte metro
  '287': { lat: 35.5951, lng: -82.5515 }, // Asheville
  '288': { lat: 35.5951, lng: -82.5515 }, // Asheville metro
  '286': { lat: 35.7332, lng: -81.3412 }, // Hickory
  '270': { lat: 36.0999, lng: -80.2442 }, // Winston-Salem area
  '271': { lat: 36.0999, lng: -80.2442 }, // Winston-Salem metro
  '272': { lat: 36.0726, lng: -79.7920 }, // Greensboro area
  '273': { lat: 36.0726, lng: -79.7920 }, // Greensboro area
  '274': { lat: 36.0726, lng: -79.7920 }, // Greensboro metro
  '275': { lat: 35.7796, lng: -78.6382 }, // Raleigh area
  '276': { lat: 35.7796, lng: -78.6382 }, // Raleigh metro
  '277': { lat: 35.9940, lng: -78.8986 }, // Durham
  '284': { lat: 34.2257, lng: -77.9447 }, // Wilmington
  '285': { lat: 35.1085, lng: -77.0441 }, // New Bern
  '278': { lat: 35.9382, lng: -77.7905 }, // Rocky Mount
  '279': { lat: 36.2946, lng: -76.2511 }, // Elizabeth City / Outer Banks

  // Texas
  '770': { lat: 29.7604, lng: -95.3698 }, // Houston
  '772': { lat: 29.7604, lng: -95.3698 }, // Houston
  '773': { lat: 30.1658, lng: -95.4613 }, // The Woodlands / Conroe
  '774': { lat: 29.5694, lng: -95.7647 }, // Sugar Land / Katy
  '775': { lat: 29.3013, lng: -94.7977 }, // Galveston / Baytown
  '776': { lat: 30.0860, lng: -94.1018 }, // Beaumont area
  '777': { lat: 30.0860, lng: -94.1018 }, // Beaumont metro
  '750': { lat: 32.7767, lng: -96.7970 }, // Dallas area (Plano/Frisco)
  '751': { lat: 32.7767, lng: -96.7970 }, // Dallas area
  '752': { lat: 32.7767, lng: -96.7970 }, // Dallas metro
  '753': { lat: 32.7767, lng: -96.7970 }, // Dallas
  '760': { lat: 32.7555, lng: -97.3308 }, // Fort Worth area (Arlington)
  '761': { lat: 32.7555, lng: -97.3308 }, // Fort Worth metro
  '762': { lat: 33.2148, lng: -97.1331 }, // Denton
  '765': { lat: 31.0982, lng: -97.3428 }, // Temple / Killeen
  '766': { lat: 31.5493, lng: -97.1467 }, // Waco area
  '767': { lat: 31.5493, lng: -97.1467 }, // Waco metro
  '786': { lat: 30.2672, lng: -97.7431 }, // Austin area
  '787': { lat: 30.2672, lng: -97.7431 }, // Austin metro
  '780': { lat: 29.4241, lng: -98.4936 }, // San Antonio area
  '781': { lat: 29.4241, lng: -98.4936 }, // San Antonio area
  '782': { lat: 29.4241, lng: -98.4936 }, // San Antonio metro
  '784': { lat: 27.8006, lng: -97.3964 }, // Corpus Christi
  '785': { lat: 26.2034, lng: -98.2300 }, // McAllen / Brownsville
  '794': { lat: 33.5779, lng: -101.8552 }, // Lubbock
  '799': { lat: 31.7619, lng: -106.4850 }, // El Paso
}

// Fallback regional centroids by first 1-2 digits
function getRegionalFallback(zip: string): LatLng {
  const p1 = zip.charAt(0)
  const p2 = zip.slice(0, 2)

  // Specific 2-digit fallbacks
  const p2Map: Record<string, LatLng> = {
    '01': { lat: 42.3601, lng: -71.0589 }, // MA
    '02': { lat: 42.3601, lng: -71.0589 }, // MA/RI
    '03': { lat: 43.2081, lng: -71.5376 }, // NH
    '04': { lat: 44.3106, lng: -69.7795 }, // ME
    '05': { lat: 44.2601, lng: -72.5754 }, // VT
    '06': { lat: 41.7658, lng: -72.6734 }, // CT
    '07': { lat: 40.7357, lng: -74.1724 }, // NJ North
    '08': { lat: 39.9526, lng: -74.1918 }, // NJ South
    '10': { lat: 40.7128, lng: -74.0060 }, // NYC
    '11': { lat: 40.7128, lng: -73.9352 }, // Long Island / Queens
    '12': { lat: 42.6526, lng: -73.7562 }, // Albany, NY
    '13': { lat: 43.0481, lng: -76.1474 }, // Syracuse, NY
    '14': { lat: 42.8864, lng: -78.8784 }, // Buffalo, NY
    '15': { lat: 40.4406, lng: -79.9959 }, // Pittsburgh, PA
    '16': { lat: 42.1292, lng: -80.0851 }, // Erie, PA
    '17': { lat: 40.2732, lng: -76.8867 }, // Harrisburg, PA
    '18': { lat: 40.6084, lng: -75.4902 }, // Allentown, PA
    '19': { lat: 39.9526, lng: -75.1652 }, // Philadelphia, PA
    '20': { lat: 38.9072, lng: -77.0369 }, // DC / Northern VA
    '21': { lat: 39.2904, lng: -76.6122 }, // Baltimore, MD
    '22': { lat: 38.8462, lng: -77.3064 }, // Northern VA
    '23': { lat: 37.5407, lng: -77.4360 }, // Richmond, VA
    '24': { lat: 37.2707, lng: -79.9414 }, // Roanoke, VA
    '25': { lat: 38.3498, lng: -81.6326 }, // Charleston, WV
    '40': { lat: 38.2527, lng: -85.7585 }, // Louisville, KY
    '41': { lat: 38.0406, lng: -84.5037 }, // Lexington, KY
    '43': { lat: 39.9612, lng: -82.9988 }, // Columbus, OH
    '44': { lat: 41.4993, lng: -81.6944 }, // Cleveland, OH
    '45': { lat: 39.1031, lng: -84.5120 }, // Cincinnati, OH
    '46': { lat: 39.7684, lng: -86.1581 }, // Indianapolis, IN
    '48': { lat: 42.3314, lng: -83.0458 }, // Detroit, MI
    '49': { lat: 42.9634, lng: -85.6681 }, // Grand Rapids, MI
    '50': { lat: 41.5868, lng: -93.6250 }, // Des Moines, IA
    '53': { lat: 43.0389, lng: -87.9065 }, // Milwaukee, WI
    '55': { lat: 44.9778, lng: -93.2650 }, // Minneapolis, MN
    '57': { lat: 43.5460, lng: -96.7313 }, // Sioux Falls, SD
    '58': { lat: 46.8083, lng: -96.7898 }, // Fargo, ND
    '59': { lat: 45.7833, lng: -108.5007 }, // Billings, MT
    '60': { lat: 41.8781, lng: -87.6298 }, // Chicago, IL
    '61': { lat: 40.6936, lng: -89.5890 }, // Peoria, IL
    '63': { lat: 38.6270, lng: -90.1994 }, // St. Louis, MO
    '64': { lat: 39.0997, lng: -94.5786 }, // Kansas City, MO
    '66': { lat: 39.0473, lng: -95.6752 }, // Topeka / KC area, KS
    '67': { lat: 37.6872, lng: -97.3301 }, // Wichita, KS
    '68': { lat: 41.2565, lng: -95.9345 }, // Omaha, NE
    '72': { lat: 34.7465, lng: -92.2896 }, // Little Rock, AR
    '73': { lat: 35.4676, lng: -97.5164 }, // Oklahoma City, OK
    '74': { lat: 36.1540, lng: -95.9928 }, // Tulsa, OK
    '80': { lat: 39.7392, lng: -104.9903 }, // Denver, CO
    '82': { lat: 41.1400, lng: -104.8202 }, // Cheyenne, WY
    '83': { lat: 43.6150, lng: -116.2023 }, // Boise, ID
    '84': { lat: 40.7608, lng: -111.8910 }, // Salt Lake City, UT
    '85': { lat: 33.4484, lng: -112.0740 }, // Phoenix, AZ
    '87': { lat: 35.0844, lng: -106.6504 }, // Albuquerque, NM
    '89': { lat: 36.1699, lng: -115.1398 }, // Las Vegas, NV
    '90': { lat: 34.0522, lng: -118.2437 }, // Los Angeles, CA
    '91': { lat: 34.1808, lng: -118.3090 }, // Glendale / Pasadena, CA
    '92': { lat: 32.7157, lng: -117.1611 }, // San Diego, CA
    '93': { lat: 35.3733, lng: -119.0187 }, // Bakersfield / Fresno, CA
    '94': { lat: 37.7749, lng: -122.4194 }, // San Francisco, CA
    '95': { lat: 38.5816, lng: -121.4944 }, // Sacramento, CA
    '97': { lat: 45.5152, lng: -122.6784 }, // Portland, OR
    '98': { lat: 47.6062, lng: -122.3321 }, // Seattle, WA
    '99': { lat: 61.2181, lng: -149.9003 }, // Anchorage, AK
  }

  if (p2Map[p2]) return p2Map[p2]

  // Broad 1-digit fallbacks
  const p1Map: Record<string, LatLng> = {
    '0': { lat: 41.5000, lng: -72.0000 }, // New England
    '1': { lat: 40.7000, lng: -75.0000 }, // NY / PA
    '2': { lat: 37.0000, lng: -78.0000 }, // Mid-Atlantic
    '3': { lat: 32.5000, lng: -84.5000 }, // Southeast
    '4': { lat: 40.0000, lng: -83.5000 }, // Midwest East
    '5': { lat: 44.0000, lng: -92.0000 }, // Upper Midwest
    '6': { lat: 39.0000, lng: -92.0000 }, // Central Midwest
    '7': { lat: 31.0000, lng: -95.0000 }, // South Central / Texas
    '8': { lat: 37.0000, lng: -108.0000 }, // Mountain West
    '9': { lat: 36.0000, lng: -119.0000 }, // West Coast
  }

  return p1Map[p1] || { lat: 30.3949, lng: -86.2286 }
}

export function getZipCoordinates(rawZip?: string): LatLng {
  if (!rawZip) return { lat: BLISS_HOME_BASE.lat, lng: BLISS_HOME_BASE.lng }
  const clean = rawZip.replace(/\D/g, '').slice(0, 5)
  if (!clean || clean.length < 3) return { lat: BLISS_HOME_BASE.lat, lng: BLISS_HOME_BASE.lng }

  if (clean === '32459') return { lat: BLISS_HOME_BASE.lat, lng: BLISS_HOME_BASE.lng }

  const zip3 = clean.slice(0, 3)
  if (ZIP3_COORDINATES[zip3]) {
    return ZIP3_COORDINATES[zip3]
  }

  return getRegionalFallback(clean)
}

export interface BlissDeliveryInfo {
  zip: string
  distanceMiles: number
  isLocal: boolean
  deliveryFee: number
  zoneLabel: string
  distanceDescription: string
  shortLabel: string
}

/**
 * Calculates delivery distance in miles and delivery/freight fee from shop in Santa Rosa Beach, FL 32459.
 */
export function calculateBlissDeliveryFromZip(rawZip?: string, totalUnits: number = 1): BlissDeliveryInfo {
  const cleanZip = (rawZip || '').replace(/\D/g, '').slice(0, 5)
  const effectiveZip = cleanZip.length === 5 ? cleanZip : BLISS_HOME_BASE.zip

  // Direct check for home zip or local 324 prefix
  if (effectiveZip === '32459' || effectiveZip.startsWith('324')) {
    return {
      zip: effectiveZip,
      distanceMiles: 0,
      isLocal: true,
      deliveryFee: 0,
      zoneLabel: 'Local (Santa Rosa Beach / 30A area)',
      distanceDescription: 'Local delivery included (within Walton County / 30A area)',
      shortLabel: 'Local (0 mi)',
    }
  }

  const coords = getZipCoordinates(effectiveZip)
  const distanceMiles = haversineDistanceMiles(
    BLISS_HOME_BASE.lat,
    BLISS_HOME_BASE.lng,
    coords.lat,
    coords.lng,
  )

  const units = Math.max(1, totalUnits)

  // Under 50 miles = local delivery included
  if (distanceMiles <= 50) {
    return {
      zip: effectiveZip,
      distanceMiles,
      isLocal: true,
      deliveryFee: 0,
      zoneLabel: 'Local (Florida Panhandle area)',
      distanceDescription: `Local delivery included (~${distanceMiles} mi from Santa Rosa Beach, FL 32459)`,
      shortLabel: `Local (~${distanceMiles} mi)`,
    }
  }

  // 51 to 300 miles = Regional Southeast (e.g. Pensacola, Tallahassee, Mobile, Atlanta, Jacksonville)
  if (distanceMiles <= 300) {
    const fee = 500 + (units > 1 ? (units - 1) * 200 : 0)
    return {
      zip: effectiveZip,
      distanceMiles,
      isLocal: false,
      deliveryFee: fee,
      zoneLabel: 'Regional Southeast Freight',
      distanceDescription: `~${distanceMiles} mi from Santa Rosa Beach, FL 32459 (Regional Freight)`,
      shortLabel: `~${distanceMiles} mi from 32459`,
    }
  }

  // 301 to 750 miles = Mid-Range Freight (e.g. Charlotte, Nashville, Orlando, Miami, Houston)
  if (distanceMiles <= 750) {
    const fee = 750 + (units > 1 ? (units - 1) * 250 : 0)
    return {
      zip: effectiveZip,
      distanceMiles,
      isLocal: false,
      deliveryFee: fee,
      zoneLabel: 'Mid-Range Freight',
      distanceDescription: `~${distanceMiles} mi from Santa Rosa Beach, FL 32459 (Mid-Range Freight)`,
      shortLabel: `~${distanceMiles} mi from 32459`,
    }
  }

  // 751 to 1,200 miles = National Freight (e.g. Dallas, Chicago, New York, DC)
  if (distanceMiles <= 1200) {
    const fee = 1000 + (units > 1 ? (units - 1) * 300 : 0)
    return {
      zip: effectiveZip,
      distanceMiles,
      isLocal: false,
      deliveryFee: fee,
      zoneLabel: 'National Freight',
      distanceDescription: `~${distanceMiles} mi from Santa Rosa Beach, FL 32459 (National Freight)`,
      shortLabel: `~${distanceMiles} mi from 32459`,
    }
  }

  // > 1,200 miles = Cross-Country Freight (e.g. Denver, Phoenix, California, Seattle)
  const fee = 1500 + (units > 1 ? (units - 1) * 400 : 0)
  return {
    zip: effectiveZip,
    distanceMiles,
    isLocal: false,
    deliveryFee: fee,
    zoneLabel: 'Cross-Country Freight',
    distanceDescription: `~${distanceMiles} mi from Santa Rosa Beach, FL 32459 (Cross-Country Freight)`,
    shortLabel: `~${distanceMiles} mi from 32459`,
  }
}
